import { X } from "lucide-react";
import type React from "react";
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const BasicModalFloatingLayerContext = createContext<HTMLElement | null>(null);

export const useBasicModalFloatingLayer = () =>
  useContext(BasicModalFloatingLayerContext);

interface BasicModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  maxWidth?: string;
  maxHeight?: string;
  showCloseButton?: boolean;
  overlayClassName?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
}

export const BasicModal: React.FC<BasicModalProps> = ({
  isOpen,
  onClose,
  children,
  title = "Dialog",
  maxWidth = "max-w-lg",
  maxHeight,
  showCloseButton = true,
  overlayClassName,
  contentClassName,
  contentStyle,
}) => {
  const [floatingLayerElement, setFloatingLayerElement] =
    useState<HTMLDivElement | null>(null);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        hideCloseButton
        overlayClassName={cn(
          "z-[var(--z-basic-modal-overlay)] border border-black/50 bg-black/40 backdrop-blur-sm",
          overlayClassName,
        )}
        className={cn(
          "z-[var(--z-basic-modal-content)] flex max-h-[90vh] w-full flex-col gap-0 overflow-visible rounded-tr-xl border-0 bg-white p-0 shadow-none",
          maxWidth,
          maxHeight,
          contentClassName,
        )}
        style={contentStyle}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>

        {showCloseButton && (
          <Button
            onClick={onClose}
            variant="ghost"
            className="absolute right-4 top-4 z-20 bg-white/70 backdrop-blur-sm hover:bg-white"
          >
            <X className="size-5" />
          </Button>
        )}

        <BasicModalFloatingLayerContext.Provider value={floatingLayerElement}>
          <div className="min-h-0 flex-1 overflow-auto rounded-[inherit]">
            {children}
          </div>
        </BasicModalFloatingLayerContext.Provider>
        <div ref={setFloatingLayerElement} />
      </DialogContent>
    </Dialog>
  );
};
